import React, { useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSystem } from '@ohif/core';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  Icons,
} from '@ohif/ui-next';
import { useDownloadExportStore } from '../../stores/useDownloadExportStore';
import { useJpegDownloadSelectionStore } from '../../stores/useJpegDownloadSelectionStore';
import {
  addCurrentImageToJpegSelection,
  downloadCurrentJpeg,
  downloadDicomZip,
  downloadSelectedJpegHandler,
  downloadStudyJpeg,
  DOWNLOAD_MESSAGES,
} from './downloadHandlers';
import type { DownloadExportKind } from '../../stores/useDownloadExportStore';

type ViewportDownloadMenuProps = {
  viewportId: string;
  onActionComplete?: () => void;
  onExportComplete?: () => void;
  variant?: 'default' | 'studyBrowser';
};

function ViewportDownloadMenu({
  viewportId,
  onActionComplete,
  onExportComplete,
  variant = 'default',
}: ViewportDownloadMenuProps) {
  const { t } = useTranslation('ViewportDownload');
  const { servicesManager, extensionManager } = useSystem();

  const isExporting = useDownloadExportStore(state => state.isExporting);
  const exportKind = useDownloadExportStore(state => state.exportKind);
  const exportProgressLabel = useDownloadExportStore(state => state.progressLabel);

  const isSelectionMode = useJpegDownloadSelectionStore(state => state.isSelectionMode);
  const selectedCount = useJpegDownloadSelectionStore(state => state.selectedImageIds.length);
  const toggleSelectionMode = useJpegDownloadSelectionStore(state => state.toggleSelectionMode);
  const clearSelection = useJpegDownloadSelectionStore(state => state.clearSelection);

  const runExport = useCallback(
    async (task: () => Promise<{ skipped?: boolean }>) => {
      const result = await task();

      if (!result?.skipped) {
        onExportComplete?.();
      }
    },
    [onExportComplete]
  );

  const handleDownloadDicomZip = useCallback(() => {
    return runExport(() =>
      downloadDicomZip({
        viewportId,
        servicesManager,
        extensionManager,
      })
    );
  }, [extensionManager, runExport, servicesManager, viewportId]);

  const handleDownloadCurrentJpeg = useCallback(() => {
    return runExport(() =>
      downloadCurrentJpeg({
        viewportId,
        servicesManager,
        extensionManager,
      })
    );
  }, [extensionManager, runExport, servicesManager, viewportId]);

  const handleDownloadStudyJpeg = useCallback(() => {
    return runExport(() =>
      downloadStudyJpeg({
        viewportId,
        servicesManager,
        extensionManager,
      })
    );
  }, [extensionManager, runExport, servicesManager, viewportId]);

  const handleDownloadSelectedJpeg = useCallback(() => {
    return runExport(() =>
      downloadSelectedJpegHandler({
        viewportId,
        servicesManager,
        extensionManager,
      })
    );
  }, [extensionManager, runExport, servicesManager, viewportId]);

  const handleToggleSelectionMode = useCallback(() => {
    if (isExporting) {
      return;
    }

    toggleSelectionMode();
    onActionComplete?.();
  }, [isExporting, onActionComplete, toggleSelectionMode]);

  const handleAddCurrentImage = useCallback(() => {
    if (isExporting) {
      return;
    }

    addCurrentImageToJpegSelection({
      viewportId,
      servicesManager,
    });
    onActionComplete?.();
  }, [isExporting, onActionComplete, servicesManager, viewportId]);

  const handleClearSelection = useCallback(() => {
    if (isExporting) {
      return;
    }

    clearSelection();
    onActionComplete?.();
  }, [clearSelection, isExporting, onActionComplete]);

  const isStudyBrowser = variant === 'studyBrowser';
  const iconClassName = isStudyBrowser ? 'text-[#0891B2]' : 'text-foreground';
  const labelClassName = isStudyBrowser
    ? 'px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#0891B2]'
    : 'text-muted-foreground px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide';
  const statusClassName = isStudyBrowser
    ? 'flex items-center gap-2 px-2 py-1.5 text-[11px] text-[#64748b]'
    : 'text-muted-foreground flex items-center gap-2 px-2 py-1.5 text-[11px]';

  const renderLeadingIcon = (kind: DownloadExportKind, defaultIcon: React.ReactNode) => {
    if (exportKind === kind) {
      return (
        <Icons.LoadingSpinner
          className={classnames(iconClassName, 'h-4 w-4 shrink-0 animate-spin')}
        />
      );
    }

    return defaultIcon;
  };

  const getExportLabel = (kind: DownloadExportKind, defaultLabel: string) => {
    if (exportKind === kind) {
      return exportProgressLabel || defaultLabel;
    }

    return defaultLabel;
  };

  const selectedJpegLabel =
    selectedCount > 0
      ? t('DownloadSelectedJpegCount', { count: selectedCount })
      : t('DownloadSelectedJpeg');

  const menuItemClassName = classnames(
    'viewport-download-menu__item cursor-pointer',
    isStudyBrowser &&
      'text-[#334155] focus:bg-[#0891B2]/10 focus:text-[#0891B2] data-[highlighted]:bg-[#0891B2]/10 data-[highlighted]:text-[#0891B2]',
    isExporting && 'pointer-events-none opacity-60'
  );

  return (
    <>
      <DropdownMenuLabel className={labelClassName}>{t('Download')}</DropdownMenuLabel>

      {isExporting && (
        <div
          className={statusClassName}
          data-cy="download-export-status"
        >
          <Icons.LoadingSpinner className="h-3.5 w-3.5 shrink-0 animate-spin text-[#0891B2]" />
          <span>{exportProgressLabel || DOWNLOAD_MESSAGES.PREPARING_JPEG}</span>
        </div>
      )}

      <DropdownMenuItem
        className={menuItemClassName}
        data-cy="download-dicom-zip"
        disabled={isExporting}
        onSelect={event => {
          event.preventDefault();
          void handleDownloadDicomZip();
        }}
      >
        {renderLeadingIcon(
          'dicom',
          <Icons.Download className={classnames(iconClassName, 'h-4 w-4 shrink-0')} />
        )}
        <span className="pl-2">{getExportLabel('dicom', t('DownloadDicomZip'))}</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        className={menuItemClassName}
        data-cy="download-jpeg-current"
        disabled={isExporting}
        onSelect={event => {
          event.preventDefault();
          void handleDownloadCurrentJpeg();
        }}
      >
        {renderLeadingIcon(
          'jpeg-current',
          <Icons.ToolCapture className={classnames(iconClassName, 'h-4 w-4 shrink-0')} />
        )}
        <span className="pl-2">{getExportLabel('jpeg-current', t('DownloadJpegCurrent'))}</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        className={menuItemClassName}
        data-cy="download-jpeg-study"
        disabled={isExporting}
        onSelect={event => {
          event.preventDefault();
          void handleDownloadStudyJpeg();
        }}
      >
        {renderLeadingIcon(
          'jpeg-study',
          <Icons.Download className={classnames(iconClassName, 'h-4 w-4 shrink-0')} />
        )}
        <span className="pl-2">{getExportLabel('jpeg-study', t('DownloadJpegStudy'))}</span>
      </DropdownMenuItem>

      <DropdownMenuSeparator className={isStudyBrowser ? 'bg-[#e5e5e5]' : undefined} />

      <DropdownMenuItem
        className={menuItemClassName}
        data-cy="toggle-jpeg-selection-mode"
        disabled={isExporting}
        onSelect={event => {
          event.preventDefault();
          handleToggleSelectionMode();
        }}
      >
        {isSelectionMode ? (
          <Icons.CheckBoxChecked className={classnames(iconClassName, 'h-4 w-4 shrink-0')} />
        ) : (
          <Icons.CheckBoxUnchecked className={classnames(iconClassName, 'h-4 w-4 shrink-0')} />
        )}
        <span className="pl-2">{t('SelectImagesForJpeg')}</span>
      </DropdownMenuItem>

      {isSelectionMode && (
        <DropdownMenuItem
          className={menuItemClassName}
          data-cy="add-current-image-to-jpeg-selection"
          disabled={isExporting}
          onSelect={event => {
            event.preventDefault();
            handleAddCurrentImage();
          }}
        >
          <Icons.Add className={classnames(iconClassName, 'h-4 w-4 shrink-0')} />
          <span className="pl-2">{t('AddCurrentImageToSelection')}</span>
        </DropdownMenuItem>
      )}

      <DropdownMenuItem
        className={classnames(
          menuItemClassName,
          selectedCount === 0 && 'pointer-events-none opacity-60'
        )}
        data-cy="download-selected-jpeg"
        disabled={isExporting || selectedCount === 0}
        onSelect={event => {
          event.preventDefault();
          void handleDownloadSelectedJpeg();
        }}
      >
        {renderLeadingIcon(
          'jpeg-selected',
          <Icons.Download className={classnames(iconClassName, 'h-4 w-4 shrink-0')} />
        )}
        <span className="pl-2">{getExportLabel('jpeg-selected', selectedJpegLabel)}</span>
      </DropdownMenuItem>

      {selectedCount > 0 && (
        <DropdownMenuItem
          className={menuItemClassName}
          data-cy="clear-jpeg-selection"
          disabled={isExporting}
          onSelect={event => {
            event.preventDefault();
            handleClearSelection();
          }}
        >
          <Icons.Cancel className={classnames(iconClassName, 'h-4 w-4 shrink-0')} />
          <span className="pl-2">{t('ClearSelection')}</span>
        </DropdownMenuItem>
      )}
    </>
  );
}

export default ViewportDownloadMenu;
